import * as React from 'react';
import type { EventCalendarParameters } from './EventCalendarStore.types';

export function useExtractEventCalendarParameters<
  TEvent extends object,
  TResource extends object,
  P extends EventCalendarParameters<TEvent, TResource>,
>(props: P): UseExtractEventCalendarParametersReturnValue<TEvent, TResource, P> {
  const {
    areEventsDraggable,
    areEventsResizable,
    canDragEventsFromTheOutside,
    canDropEventsToTheOutside,
    collapsedResources,
    dateLocale,
    defaultCollapsedResources,
    defaultPreferences,
    defaultView,
    defaultVisibleDate,
    defaultVisibleResources,
    displayTimezone,
    eventColor,
    eventCreation,
    eventDeletion,
    eventModelStructure,
    events,
    onCollapsedResourcesChange,
    onEventEditingStart,
    onEventsChange,
    onPreferencesChange,
    onViewChange,
    onVisibleDateChange,
    onVisibleResourcesChange,
    preferences,
    preferencesMenuConfig,
    readOnly,
    shouldEventRequireResource,
    resourceModelStructure,
    resources,
    showCurrentTimeIndicator,
    view,
    viewConfig,
    views,
    visibleDate,
    visibleResources,
    ...forwardedProps
  } = props;

  const parameters: EventCalendarParameters<TEvent, TResource> = React.useMemo(
    () => ({
      areEventsDraggable,
      areEventsResizable,
      canDragEventsFromTheOutside,
      canDropEventsToTheOutside,
      collapsedResources,
      dateLocale,
      defaultCollapsedResources,
      defaultPreferences,
      defaultView,
      defaultVisibleDate,
      defaultVisibleResources,
      displayTimezone,
      eventColor,
      eventCreation,
      eventDeletion,
      eventModelStructure,
      events,
      onCollapsedResourcesChange,
      onEventEditingStart,
      onEventsChange,
      onPreferencesChange,
      onViewChange,
      onVisibleDateChange,
      onVisibleResourcesChange,
      preferences,
      preferencesMenuConfig,
      readOnly,
      shouldEventRequireResource,
      resourceModelStructure,
      resources,
      showCurrentTimeIndicator,
      view,
      viewConfig,
      views,
      visibleDate,
      visibleResources,
    }),
    [
      areEventsDraggable,
      areEventsResizable,
      canDragEventsFromTheOutside,
      canDropEventsToTheOutside,
      collapsedResources,
      dateLocale,
      defaultCollapsedResources,
      defaultPreferences,
      defaultView,
      defaultVisibleDate,
      defaultVisibleResources,
      displayTimezone,
      eventColor,
      eventCreation,
      eventDeletion,
      eventModelStructure,
      events,
      onCollapsedResourcesChange,
      onEventEditingStart,
      onEventsChange,
      onPreferencesChange,
      onViewChange,
      onVisibleDateChange,
      onVisibleResourcesChange,
      preferences,
      preferencesMenuConfig,
      readOnly,
      shouldEventRequireResource,
      resourceModelStructure,
      resources,
      showCurrentTimeIndicator,
      view,
      viewConfig,
      views,
      visibleDate,
      visibleResources,
    ],
  ) satisfies Record<keyof EventCalendarParameters<TEvent, TResource>, unknown>;

  return {
    parameters,
    forwardedProps: forwardedProps as Omit<P, keyof EventCalendarParameters<TEvent, TResource>>,
  };
}

interface UseExtractEventCalendarParametersReturnValue<
  TEvent extends object,
  TResource extends object,
  P extends EventCalendarParameters<TEvent, TResource>,
> {
  parameters: EventCalendarParameters<TEvent, TResource>;
  forwardedProps: Omit<P, keyof EventCalendarParameters<TEvent, TResource>>;
}
